import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentStatusService } from '../../services/payment/payment-status.service';
import { environment } from '../../../environments/environment';

type PaymentStatus = 'success' | 'pending' | 'failed' | 'rejected' | 'canceled' | 'error' | undefined;

type IconImg = { kind: 'img'; src: string; alt: string };
type IconEmoji = { kind: 'emoji'; text: string };
type IconData = IconImg | IconEmoji;

@Component({
  selector: 'app-payment-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-status.component.html',
  styleUrls: ['./payment-status.component.scss']
})
export class PaymentStatusComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly paymentService = inject(PaymentStatusService);

  // Los pagos asíncronos (PSE, transferencia Bancolombia) llegan aquí todavía
  // PENDING: la aprobación entra segundos o minutos después. Se sondea hasta
  // obtener un estado terminal en vez de mostrar un error falso.
  private static readonly POLL_INTERVAL_MS = 5000;
  private static readonly MAX_POLL_ATTEMPTS = 36; // ~3 minutos

  private pollAttempts = 0;
  private pollTimer: ReturnType<typeof setTimeout> | undefined;
  private transactionId: string | null = null;

  readonly status = signal<PaymentStatus>(undefined);

  readonly title = computed(() => {
    switch (this.status()) {
      case 'success':
        return 'Pago confirmado';
      case 'pending':
        return 'Tu pago está siendo procesado';
      case 'failed':
      case 'error':
        return 'No pudimos procesar tu pago';
      case 'rejected':
        return 'Tu pago fue rechazado';
      case 'canceled':
        return 'Pago cancelado';
      default:
        return 'Estado de pago desconocido';
    }
  });

  readonly description = computed(() => {
    switch (this.status()) {
      case 'success':
        return 'Tu pago se registró exitosamente. Gracias.';
      case 'pending':
        return 'Tu transacción está en curso. Esto puede tardar unos minutos. Cuando se confirme, esta pantalla se actualizará y también te avisaremos con una notificación.';
      case 'failed':
      case 'error':
        return 'Algo salió mal con la transacción. Verifica tu método de pago e intenta nuevamente.';
      case 'rejected':
        return 'No se ha aprobado la transacción realizada. Verifica que tengas saldo disponible o cambia el método de pago.';
      case 'canceled':
        return 'Has cancelado el proceso de pago. Puedes intentarlo de nuevo cuando quieras.';
      default:
        return 'No pudimos determinar el estado del pago.';
    }
  });

  readonly icon = computed<IconData>(() => {
    switch (this.status()) {
      case 'success':
        return {
          kind: 'img',
          src: `${environment.cloudFrontUrl}/payments/icons/successfully.png`,
          alt: 'Pago confirmado',
        };
      case 'pending':
        return {
          kind: 'img',
          src: `${environment.cloudFrontUrl}/payments/icons/processing.png`,
          alt: 'Pago en proceso',
        };
      case 'failed':
      case 'error':
        return {
          kind: 'img',
          src: `${environment.cloudFrontUrl}/payments/icons/notprocessing.png`,
          alt: 'Pago no procesado',
        };
      case 'rejected':
        return {
          kind: 'img',
          src: `${environment.cloudFrontUrl}/payments/icons/rejected.png`,
          alt: 'Pago rechazado',
        };
      case 'canceled':
        return { kind: 'emoji', text: '🚫' };
      default:
        return { kind: 'emoji', text: '❔' };
    }
  });

  isImgIcon(value: IconData): value is IconImg {
    return value.kind === 'img';
  }

  isEmojiIcon(value: IconData): value is IconEmoji {
    return value.kind === 'emoji';
  }


  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const ids = params.getAll('id');
      this.transactionId = ids.at(-1) || params.get('transactionId') || params.get('tid');

      const existingStatusParam = params.get('status');
      if (existingStatusParam) {
        const mappedFromParam = this.mapWompiToUiStatus(existingStatusParam);
        // Solo los estados terminales quedan fijos. Un ?status=PENDING en la URL
        // (o uno no reconocido) no puede congelar la pantalla: se sigue sondeando.
        if (mappedFromParam && mappedFromParam !== 'pending') {
          this.status.set(mappedFromParam);
          return;
        }
      }

      console.log('[PaymentStatus] query ids:', ids, 'selected transactionId:', this.transactionId);
      if (!this.transactionId) {
        this.status.set(undefined);
        console.warn('[PaymentStatus] no transaction id provided in query params');
        return;
      }

      this.status.set('pending');
      this.pollAttempts = 0;
      this.checkStatus();
    });
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }

  /**
   * Consulta el estado en el MS de pagos (que a su vez consulta Wompi y
   * actualiza los registros). PENDING → reintenta; error de red → reintenta
   * (el error transitorio no es un pago fallido); estado terminal → se fija
   * en la URL para que la app lo detecte.
   */
  private checkStatus(): void {
    if (!this.transactionId) return;

    this.paymentService.getMsStatusById(this.transactionId).subscribe({
      next: (resp: { status: string }) => {
        const mapped = this.mapWompiToUiStatus(resp?.status);
        if (mapped && mapped !== 'pending') {
          this.status.set(mapped);
          this.persistTerminalStatusInUrl(resp?.status);
          return;
        }
        this.status.set('pending');
        this.scheduleNextPoll();
      },
      error: () => {
        // No degradar a error por una consulta fallida: el pago puede ir bien.
        this.scheduleNextPoll();
      },
    });
  }

  private scheduleNextPoll(): void {
    if (this.pollAttempts >= PaymentStatusComponent.MAX_POLL_ATTEMPTS) {
      // Se agotó la espera: se mantiene "pendiente" (el backend concilia y
      // notifica por push cuando el pago entre), nunca un error falso.
      this.status.set('pending');
      return;
    }
    this.pollAttempts++;
    this.pollTimer = setTimeout(() => this.checkStatus(), PaymentStatusComponent.POLL_INTERVAL_MS);
  }

  /**
   * Fija ?status=<terminal> y recarga una sola vez. La app móvil detecta
   * status=APPROVED|DECLINED|ERROR en la URL del webview para mostrar su UI.
   */
  private persistTerminalStatusInUrl(rawStatus: string | undefined): void {
    if (!rawStatus) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('status') !== rawStatus) {
      url.searchParams.set('status', rawStatus);
      window.location.replace(url.toString());
    }
  }

  onPrimaryAction(): void {
    const s = this.status();
    if (s === 'failed' || s === 'error' || s === 'rejected') {
      // En un caso real podríamos redirigir al flujo de pago; por ahora al dashboard
      this.router.navigate(['/dashboard']);
      return;
    }
    // Para pending/success/canceled o desconocido
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private mapWompiToUiStatus(wompi: string | undefined): PaymentStatus {
    if (!wompi) return undefined;
    const normalized = wompi.toUpperCase();
    switch (normalized) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'pending';
      case 'DECLINED':
      case 'VOIDED':
        return 'rejected';
      case 'ERROR':
      case 'FAILED':
        return 'error';
      default:
        return undefined;
    }
  }
}

